/**
 * Tickets — the local per-event ticket index: every ticket document for the
 * door's selected event, kept warm in memory for zero-hop identity/status
 * resolution. Pure state — no Firestore import (the io-in-view-or-state
 * guard); tickets.sync.ts is the sole writer, driven by one live, unfiltered
 * listener scoped to event.state.ts's `selectedEventId`.
 *
 * `live` is the listener's own freshness signal: true only while the most
 * recent snapshot it emitted was server-acked (`!snapshot.metadata.fromCache`)
 * — the same signal verdict.sync.ts's `ackedQuerySnapshot$` already keys off.
 * A negative verdict gated on a MUTABLE field (`status`) may render from this
 * index only while `live` is true; a field that can never change post-mint
 * (`typeId`) carries no such requirement. Never persisted — a stale flag
 * surviving a reload would misrepresent a fresh session's own connectivity.
 *
 * `byOrderId` is maintained alongside `byQrToken`/`byTicketId` (not
 * recomputed by scanning the whole index) so party-context lookups — which
 * siblings share this ticket's order, and their own current status — stay
 * O(1) even at a few thousand tickets.
 */
import { computed, state } from "@mhmo91/schmancy/state";
import type { Ticket } from "@momo/types";
import { organizationId } from "../../workspace/organization.state.ts";
import { selectedEventId } from "../event/event.state.ts";
import type { PartyContext } from "../verdict/verdict.state.ts";

export type IndexedTicket = Ticket.Ticket & { readonly ticketId: string };

export type TicketRows = {
	readonly byQrToken: ReadonlyMap<string, IndexedTicket>;
	readonly byTicketId: ReadonlyMap<string, IndexedTicket>;
	readonly byOrderId: ReadonlyMap<string, readonly string[]>;
};

/** The organization and event an index answer was read under. */
export type IndexScope = {
	readonly orgId: string | null;
	readonly eventId: string | null;
};

/** One read's whole standing, tagged with the scope it belongs to, and
 *  written in one replace so the rows and the claim about them never part:
 *  - `loading` — nothing answered yet for this scope, including straight
 *    after a switch;
 *  - `denied` — this session's permission closes the gate; no read runs;
 *  - `ready` — the read has fully loaded; `live` is false while the latest
 *    snapshot came from the local cache;
 *  - `error` — the read failed and is retrying; `last` is the last answer
 *    for this same scope, or `null` when none had landed. */
export type TicketsIndexState = IndexScope &
	(
		| { readonly status: "loading" | "denied" }
		| ({ readonly status: "ready"; readonly live: boolean } & TicketRows)
		| { readonly status: "error"; readonly last: TicketRows | null }
	);

const initial: TicketsIndexState = {
	status: "loading",
	orgId: null,
	eventId: null,
};

export const ticketsIndex = state<TicketsIndexState>(
	"scanner/tickets-index",
).memory(initial);

/** The rows every reader states from: the ready answer, or an erroring
 *  read's last answer (never live); `null` while nothing has answered for the
 *  active scope. `selectedEventId` is that scope's event on both doors: the
 *  scanner picks it, and the web console writes its own event into it. */
export const answeredTickets = computed(
	(): (TicketRows & { readonly live: boolean }) | null => {
		const index = ticketsIndex.value;
		// Rows tagged for another organization or event are not this scope's
		// answer, whichever writer has yet to re-key.
		if (
			index.orgId === null ||
			index.eventId === null ||
			index.orgId !== organizationId.value ||
			index.eventId !== selectedEventId.value
		)
			return null;
		if (index.status === "ready") return index;
		if (index.status === "error" && index.last !== null)
			return { ...index.last, live: false };
		return null;
	},
);

/** Which siblings share a ticket's order, and where each of them stands —
 *  read straight off `byOrderId`/`byTicketId`, which is what that pair is
 *  maintained for. Every surface that states a party reads it here: a second
 *  derivation over the same tickets would be free to disagree with this one
 *  about how many of a group are already inside. */
export function partyContextFor(
	orderId: string,
	ticketId: string,
): PartyContext | null {
	const tickets = answeredTickets.get();
	const siblingIds = tickets?.byOrderId.get(orderId) ?? [];
	if (siblingIds.length <= 1) return null;
	let othersRemaining = 0;
	let othersAlreadyIn = 0;
	for (const id of siblingIds) {
		if (id === ticketId) continue;
		const sibling = tickets?.byTicketId.get(id);
		if (!sibling) continue;
		if (sibling.status === "redeemed") othersAlreadyIn += 1;
		else othersRemaining += 1;
	}
	return { total: siblingIds.length, othersRemaining, othersAlreadyIn };
}
